import { ANIMATION_ICON } from '@/components/admin/experiences/page-editor/animation-picker';
import { blockRowLabel } from '@/components/admin/experiences/page-editor/block-stack';
import {
  ANIMATION_LABEL,
  PAGE_EDITOR_IDS,
} from '@/components/admin/experiences/page-editor/contracts';
import { Segmented } from '@/components/admin/experiences/page-editor/segmented';
import { Button } from '@/components/ui/button';
import { Callout } from '@/components/ui/callout';
import { SPEED_LABEL, cutOffTempsIndex, delayLabel } from '@/lib/page-editor';
import type { AnimationSpeed, ExperiencePageDraft, TempsDraft } from '@matter_extractor/shared';
import { ANIMATION_SPEED_MS, EXPERIENCE_TEMPS_DELAY_MAX } from '@matter_extractor/shared';
import { Plus, Trash2 } from 'lucide-react';
import { useId } from 'react';

/**
 * L'inspecteur d'un TEMPS, ouvert par un clic sur un séparateur (refonte
 * modulaire §9.3, H27, H30; planche `Inspecteurs`; issue #58).
 *
 * « Un temps n'a pas d'onglets ni d'animation : il porte la rapidité de ses
 * blocs, le délai avant lui, la liste de ses blocs avec leur animation,
 * "Ajouter un temps après" et "Supprimer ce temps". »
 *
 * Three sentences of H27 are rendered here rather than explained elsewhere.
 *
 *   - **Le temps 1 part avec la page** : his start is a FACT and not a control,
 *     because `experienceContentSchema` refuses anything else in first
 *     position. Offering the two choices and then refusing one of them would be
 *     a control that lies.
 *   - **« Supprimer ce temps » rend ses blocs au précédent** : the button says
 *     so under itself, and `removeTemps` is what does it - including the case
 *     the sentence does not cover, the first temps, whose blocks join the next.
 *   - **Une durée automatique plus courte que le dernier délai est SIGNALÉE**,
 *     never refused. `cutOffTempsIndex` is what answers, so the signal and the
 *     arithmetic of the pile cannot disagree.
 */

const SPEEDS: readonly { value: AnimationSpeed; label: string }[] = [
  { value: 'fast', label: 'Rapide' },
  { value: 'normal', label: 'Normal' },
  { value: 'slow', label: 'Lent' },
];

export interface TempsInspectorProps {
  readonly page: ExperiencePageDraft;
  readonly temps: TempsDraft;
  readonly index: number;
  readonly readOnly: boolean;
  readonly onTemps: (next: TempsDraft) => void;
  readonly onAddAfter: () => void;
  readonly onRemove: () => void;
}

export function TempsInspector({
  page,
  temps,
  index,
  readOnly,
  onTemps,
  onAddAfter,
  onRemove,
}: TempsInspectorProps) {
  const delayId = useId();
  const first = index === 0;
  const cut = cutOffTempsIndex(page);
  const cutHere = cut !== null && cut <= index;

  return (
    <section data-testid={PAGE_EDITOR_IDS.tempsInspector} className="space-y-4">
      <div>
        <h2 className="font-semibold text-bleu-nuit text-sm">
          Temps {index + 1} ·{' '}
          {temps.blocks.length === 1 ? '1 bloc' : `${temps.blocks.length} blocs`}
        </h2>
        <p className="text-gray-600 text-xs leading-4">
          Un temps groupe ce qui apparaît ensemble. Il n’a pas d’animation : il donne la rapidité et
          le départ, chaque bloc choisit son mouvement.
        </p>
      </div>

      <div className="space-y-1.5">
        <p className="block font-semibold text-gray-800 text-xs">Rapidité des animations</p>
        <Segmented
          label="Rapidité des animations de ce temps"
          size="sm"
          testId={PAGE_EDITOR_IDS.tempsSpeed}
          value={temps.speed}
          options={SPEEDS}
          readOnly={readOnly}
          onChange={(speed) => onTemps({ ...temps, speed })}
        />
        <p className="text-gray-600 text-xs leading-4">
          Vaut pour tous les blocs de ce temps. Rapide {ANIMATION_SPEED_MS.fast} ms · normal{' '}
          {ANIMATION_SPEED_MS.normal} ms · lent {ANIMATION_SPEED_MS.slow} ms.
        </p>
      </div>

      <div className="space-y-1.5">
        <p className="block font-semibold text-gray-800 text-xs">Départ du temps</p>
        {first ? (
          <p className="text-gray-700 text-sm">
            Le temps 1 part avec la page : son délai est {delayLabel(0)} et ne se règle pas.
          </p>
        ) : (
          <>
            <Segmented
              label="Départ du temps"
              size="sm"
              testId={PAGE_EDITOR_IDS.tempsStart}
              value={temps.start.kind}
              options={[
                { value: 'delay', label: 'Après un délai' },
                { value: 'click', label: 'Au clic d’un bouton' },
              ]}
              readOnly={readOnly}
              onChange={(kind) =>
                onTemps({
                  ...temps,
                  start: kind === 'click' ? { kind: 'click' } : { kind: 'delay', seconds: 1 },
                })
              }
            />
            {temps.start.kind === 'delay' ? (
              <div className="flex items-center gap-2">
                <label htmlFor={delayId} className="sr-only">
                  Délai avant ce temps, en secondes
                </label>
                <input
                  id={delayId}
                  type="number"
                  step="0.1"
                  min={0}
                  max={EXPERIENCE_TEMPS_DELAY_MAX}
                  data-testid={PAGE_EDITOR_IDS.tempsDelay}
                  value={temps.start.seconds}
                  readOnly={readOnly}
                  onChange={(event) => {
                    const seconds = Number(event.currentTarget.value);
                    if (Number.isNaN(seconds)) return;
                    onTemps({
                      ...temps,
                      start: {
                        kind: 'delay',
                        seconds: Math.min(Math.max(seconds, 0), EXPERIENCE_TEMPS_DELAY_MAX),
                      },
                    });
                  }}
                  className="h-9 w-24 rounded-md border-2 border-gray-300 bg-white px-3 text-sm read-only:bg-gray-50 focus-visible:border-bleu-nuit focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                />
                <span className="text-gray-600 text-sm">s</span>
              </div>
            ) : (
              <p className="text-gray-600 text-xs leading-4">
                Un bouton du temps précédent choisit « Joue le temps suivant » et remplace le délai
                (H30) ; il disparaît une fois ce temps joué.
              </p>
            )}
            <p className="text-gray-600 text-xs leading-4">
              Le délai se compte depuis la fin des animations du temps précédent.
            </p>
          </>
        )}
      </div>

      <div className="space-y-1.5">
        <p className="block font-semibold text-gray-800 text-xs">Blocs de ce temps</p>
        {temps.blocks.length === 0 ? (
          <p className="text-gray-600 text-xs leading-4">
            Aucun bloc. Un temps vidé de ses blocs disparaît dès qu’on lui en retire un.
          </p>
        ) : (
          <ul className="space-y-1">
            {temps.blocks.map((block) => {
              const Icon = ANIMATION_ICON[block.animation];
              return (
                <li
                  key={block.id}
                  className="flex items-center justify-between gap-2 rounded-md bg-gray-50 px-2 py-1.5"
                >
                  <span className="min-w-0 truncate text-gray-800 text-xs">
                    {blockRowLabel(block)}
                  </span>
                  <span className="flex shrink-0 items-center gap-1 text-gray-600 text-xs">
                    <Icon aria-hidden className="size-3.5" />
                    {block.type === 'sound'
                      ? 'sans animation'
                      : `${ANIMATION_LABEL[block.animation].toLowerCase()} · ${SPEED_LABEL[temps.speed]}`}
                  </span>
                </li>
              );
            })}
          </ul>
        )}
        <p className="text-gray-600 text-xs leading-4">
          Glisse un bloc sur un séparateur pour le changer de temps, ou choisis « Apparaît au temps
          » dans son onglet Animation.
        </p>
      </div>

      {cutHere ? (
        <Callout tone="warn" data-testid={PAGE_EDITOR_IDS.tempsCutOff}>
          La durée automatique de la page ({page.autoDurationSeconds} s) est plus courte que ce
          départ : ce temps n’apparaîtrait jamais. C’est un signal, pas un refus.
        </Callout>
      ) : null}

      <div className="flex flex-wrap gap-2">
        <Button
          type="button"
          variant="outline"
          size="sm"
          data-testid={PAGE_EDITOR_IDS.tempsAdd}
          disabled={readOnly}
          onClick={onAddAfter}
        >
          <Plus aria-hidden className="size-4" />
          Ajouter un temps après
        </Button>
        <Button
          type="button"
          variant="destructive"
          size="sm"
          data-testid={PAGE_EDITOR_IDS.tempsRemove}
          disabled={readOnly || page.temps.length <= 1}
          onClick={onRemove}
        >
          <Trash2 aria-hidden className="size-4" />
          Supprimer ce temps
        </Button>
      </div>
      <p className="text-gray-600 text-xs leading-4">
        {page.temps.length <= 1
          ? 'Le seul temps d’une page ne se supprime pas : la pile n’aurait plus où déposer un bloc.'
          : first
            ? 'Ses blocs rejoignent le temps suivant, qui part alors avec la page.'
            : 'Ses blocs rejoignent le temps précédent.'}
      </p>
    </section>
  );
}
